import PropTypes from 'prop-types';
import { Inline, Stack } from '@etg/wings';
import isEmpty from 'lodash/isEmpty';
import { css, cx, etiColors } from '@eti/styles';
import { useTranslation } from '@eti/providers';
import { CheckedBaggage } from '@eti/types';
import { Icon as EtiIcon } from '@eti/components';
import { constants, traveler as travelerUtil } from '@eti/utils';
import { getBaggageWeightText } from '../../trip-details/utils/format';
import { getTravelerCheckedBaggageSummary } from '../utils/traveler';
import ProductsList from './ProductsList';
import { BaggageText, baggageIconStyles } from './IncludedBaggageSummary';

const { unicodeControlCharacters, ageTypes } = constants;
const { LRE, PDF } = unicodeControlCharacters;

const travelersStyles = css`
  @media (min-width: 60.625em) {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
  }
`;

const travelerStyles = css`
  border-bottom: 1px dotted ${etiColors.grayScale[6]};
  padding: 0.625rem;

  &:last-child {
    border: 0;
  }

  @media (min-width: 60.625em) {
    width: calc(50% - 0.75rem);

    &:nth-last-child(2):nth-child(odd) {
      border: 0;
    }
  }
`;

const travelerNameStyles = css`
  align-items: baseline;
`;

const productListWrapperStyles = css`
  display: flex;
`;

const iconClassName = css`
  flex-shrink: 0;
`;

const ageTypeStyles = css`
  display: inline-flex;
  font-size: 0.75rem;
`;

const ageTypeToTranslationKey = {
  ADT: 'Adult',
  CNN: 'Child',
  INF: 'Infant',
};

const TravelerListTravelers = ({
  includedCheckedBaggage = {},
  isShowingProducts,
  travelers = [],
  checkedBaggageSummary,
}) => {
  const { t } = useTranslation();
  const hasIncludedBaggage = !isEmpty(includedCheckedBaggage);
  const weightText = hasIncludedBaggage
    ? `${LRE}${getBaggageWeightText(includedCheckedBaggage)}${PDF}`
    : '';

  return (
    <div className={travelersStyles}>
      {travelers.map(({ ageType, id, name, products }) => {
        const isInfant = ageType === ageTypes.INFANT;

        return (
          <Stack
            className={cx(travelerStyles, constants.MOUSE_FLOW_CLASS_NAMES)}
            data-testid={`payment-tripdetails-traveler-${id}`}
            key={id}
            spacing={4}
          >
            <Inline alignY="start" spacing={8}>
              <EtiIcon
                className={iconClassName}
                color={etiColors.grayScale[6]}
                id={travelerUtil.travelerAgeIcons[ageType]}
                size="1rem"
              />
              <Inline className={travelerNameStyles} spacing={8}>
                <span data-testid="traveler-name">{name}</span>
                <span className={ageTypeStyles} data-testid="traveler-ageType">
                  {t(ageTypeToTranslationKey[ageType])}
                </span>
                {hasIncludedBaggage && !isShowingProducts && (
                  <BaggageText>
                    <EtiIcon
                      className={baggageIconStyles}
                      color={etiColors.grayScale[8]}
                      id={isInfant ? 'no-baggage' : 'baggage-included'}
                      size="12px"
                    />
                    <span>{isInfant ? ` ${t('TripDetails.NoCheckedBaggage')}` : weightText}</span>
                  </BaggageText>
                )}
              </Inline>
            </Inline>
            {isShowingProducts && (
              <div className={productListWrapperStyles}>
                <ProductsList
                  checkedBaggageSummary={getTravelerCheckedBaggageSummary(
                    checkedBaggageSummary,
                    id,
                  )}
                  includedCheckedBaggage={includedCheckedBaggage}
                  products={products}
                />
              </div>
            )}
          </Stack>
        );
      })}
    </div>
  );
};

TravelerListTravelers.propTypes = {
  checkedBaggageSummary: PropTypes.arrayOf(CheckedBaggage),
  includedCheckedBaggage: PropTypes.shape({
    pieces: PropTypes.any,
    weight: PropTypes.number,
    weightUnit: PropTypes.string,
  }),
  isShowingProducts: PropTypes.bool,
  travelers: PropTypes.arrayOf(
    PropTypes.shape({
      ageType: PropTypes.string,
      birthDate: PropTypes.string,
      firstName: PropTypes.string,
      gender: PropTypes.string,
      id: PropTypes.string,
      lastName: PropTypes.string,
      name: PropTypes.string,
      passportNumber: PropTypes.string,
      products: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string,
          name: PropTypes.string,
          quantity: PropTypes.number,
        }),
      ),
    }),
  ),
};

export default TravelerListTravelers;
